import React, { Fragment } from "react";
import { Row, Table, Button, Col } from "reactstrap";
import { Link } from "react-router-dom";

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

const TableContainer = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  paginationWrapper,
  pagination,
  buttonClass,
  buttonName,
  isAddButton,
  isLoading,
  isCustomPageSize = false,
  handleUserClick,
  page,
  classDate,
  timeSlots = [],
  timeSlot,
  isClassCancelled,
  canShowAttendance,
  countPerPage,
  totalRecords,
  sortingState,
  onPageChange,
  onTimeSlotChange,
  onSortChange
}) => {

  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: Math.max(1, Math.ceil(totalRecords / countPerPage)),
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize: countPerPage,
      },
      sorting: sortingState,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sortingState) : updater;

      let nextSort;

      if (!sortingState.length) {
        nextSort = { id: newSorting[0]?.id, desc: false };
      } else if (!sortingState[0].desc) {
        nextSort = { id: sortingState[0].id, desc: true };
      } else {
        nextSort = { id: newSorting[0]?.id, desc: false };
      }

      if (nextSort) {
        onSortChange(nextSort.id, nextSort.desc);
      } else {
        onSortChange(nextSort.id, nextSort.desc);
      }
    },
  });

  const start = (page - 1) * countPerPage + 1;
  const end = Math.min(page * countPerPage, totalRecords);

  const today = new Date(classDate).getDay();

  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
  ];

  const todayName = dayNames[today];

  return (
    <Fragment>

      <Row className="mb-2">

        <Col sm={1} className="mt-1">
          <h5>{todayName}</h5>
        </Col>
        {isCustomPageSize && (
          <Col sm={2}>
            <select
              className="form-select pageSize mb-2"
              value={timeSlot}
              onChange={e => onTimeSlotChange(e.target.value)}
            >
              {timeSlots.map((slot, index) => {
                const slotValue = slot.Batch_Time || slot;

                return (
                  <option key={index} value={slotValue}>
                    {slotValue}
                  </option>
                );
              })}
            </select>
          </Col>
        )}

        {isAddButton && (
          <Col sm={6}>
            <div className="text-sm-end">
              <Button type="button" className={buttonClass} onClick={handleUserClick}>
                <i className="mdi mdi-plus me-1"></i> {buttonName}
              </Button>
            </div>
          </Col>
        )}
      </Row>

      {/* 📊 TABLE (UNCHANGED UI) */}
      <div className={divClassName || "table-responsive"} style={{ maxHeight: "500px", overflowY: "auto" }}>
        <Table hover className={tableClass} bordered={isBordered} style={{ borderCollapse: "separate", borderSpacing: 0, border: "1px solid #d4d4d4" }}>
          <thead className={theadClass}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className={header.column.getCanSort() ? "sorting" : ""}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      ...(header.column.id === "Student_Name"
                        ? {
                          width: "125px",
                          minWidth: "125px",
                          maxWidth: "125px",
                          whiteSpace: "normal",
                          overflowWrap: "break-word",
                        }
                        : {}),

                      cursor: header.column.getCanSort() ? "pointer" : "default",
                      position: "sticky",
                      top: 0,
                      left: header.column.columnDef.meta?.sticky ? 0 : undefined,
                      zIndex: header.column.columnDef.meta?.sticky ? 3 : 1,
                      background: header.column.columnDef.meta?.sticky ? "#fff" : undefined,
                      border: 0,
                      borderRight: "1px solid #d4d4d4",
                      borderBottom: "1px solid #d4d4d4",
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}

                    {/* Sorting Icons */}
                    {header.column.getCanSort() && (
                      <span className="ms-2">
                        {header.column.getIsSorted() === "asc" && (
                          <i className="fas fa-sort-amount-up"></i>
                        )}
                        {header.column.getIsSorted() === "desc" && (
                          <i className="fas fa-sort-amount-down"></i>
                        )}
                        {!header.column.getIsSorted() && (
                          <i className="fas fa-sort-amount-down"></i>
                        )}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  {table.getRowModel().rows.length === 0 && !isLoading && (
                    isClassCancelled ? (
                      <div>
                        <h5 className="mb-2">
                          Class Close By Admin
                        </h5>

                        <p className="text-muted mb-0">
                          This class has been closed by the admin. Please check report for more details.
                        </p>
                      </div>
                    ) : canShowAttendance ? (
                      <span>No records found</span>
                    ) : (
                      <div>
                        <h5 className="mb-2">
                          Attendance not available yet
                        </h5>

                        <p className="text-muted mb-0">
                          Attendance opens 30 minutes before batch time.
                        </p>
                      </div>
                    )
                  )}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      style={{
                        ...(cell.column.id === "Student_Name"
                          ? {
                            width: "125px",
                            minWidth: "125px",
                            maxWidth: "125px",
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                          }
                          : {}),

                        position: cell.column.columnDef.meta?.sticky ? "sticky" : "static",
                        left: cell.column.columnDef.meta?.sticky ? 0 : undefined,
                        zIndex: cell.column.columnDef.meta?.sticky ? 2 : 1,
                        background: cell.column.columnDef.meta?.sticky ? "#fff" : undefined,
                        border: 0,
                        borderRight: "1px solid #d4d4d4",
                        borderBottom: "1px solid #d4d4d4",
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* 📄 PAGINATION (UNCHANGED UI) */}
      {isPagination && totalRecords > 0 && (
        <Row className="justify-content-md-end justify-content-center align-items-center">
          <Col>
            <div className="dataTables_info">
              Showing {start} - {end} of {totalRecords} Results
            </div>
          </Col>

          <Col className="col-md-auto">
            <div className={paginationWrapper}>
              <ul className={pagination}>
                <li className={`paginate_button page-item previous ${page === 1 ? "disabled" : ""}`}>
                  <Link to="#" className="page-link" onClick={() => onPageChange(page - 1)}>
                    <i className="mdi mdi-chevron-left"></i>
                  </Link>
                </li>

                {Array.from({ length: table.getPageCount() }).map((_, index) => (
                  <li
                    key={index}
                    className={`paginate_button page-item ${page === index + 1 ? "active" : ""}`}
                  >
                    <Link to="#" className="page-link" onClick={() => onPageChange(index + 1)}>
                      {index + 1}
                    </Link>
                  </li>
                ))}

                <li className={`paginate_button page-item next ${page >= table.getPageCount() ? "disabled" : ""}`}>
                  <Link to="#" className="page-link" onClick={() => onPageChange(page + 1)}>
                    <i className="mdi mdi-chevron-right"></i>
                  </Link>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;
